import { Inject, Injectable, NgZone, Optional, DOCUMENT } from '@angular/core';

import { map, switchMap } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';

import { CssVariablesService } from '@backbase/ui-ang/css-variables-lib';

/**
 * Standard grid breakpoint identifiers for responsive design.
 *
 * @example
 *
 * ```typescript
 * // Using a standard breakpoint
 * const breakpoint: MediaBreakpoints = 'md';
 * const isMatching$ = this.mediaQueryService.isMediaBreakpointMatches('max-width', breakpoint);
 * ```
 */
export type MediaBreakpoints = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/**
 * Service for evaluating media queries and reacting to breakpoint changes.
 *
 * @remarks
 * The {@link MediaQueryService} provides utilities for detecting screen size changes
 * and managing CSS breakpoints in responsive applications. It supports both standard
 * grid breakpoints and custom pixel values for media queries.
 *
 * @example
 *
 * ```typescript
 * @Component({
 *   template: '<div>Responsive content</div>'
 * })
 * class MyComponent {
 *   constructor(private readonly mediaQueryService: MediaQueryService) {}
 *
 *   readonly isStandardMdMedia$ = this.mediaQueryService.isMediaBreakpointMatches('max-width', 'md');
 *   readonly isCustomMedia$ = this.mediaQueryService.isMediaBreakpointMatches('max-width', 500);
 * }
 * ```
 */
@Injectable()
export class MediaQueryService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Optional() private readonly cssVariablesService: CssVariablesService,
    private readonly ngZone: NgZone,
  ) {}

  /**
   * Observe media changes for current window.
   *
   * @remarks
   * Creates an observable that emits boolean values based on media query matches.
   * Supports both standard grid breakpoints (requires {@link CssVariablesService}) and
   * custom pixel values. The observable updates when the viewport size changes.
   *
   * @param type - Media query type specifying width constraint direction
   * @param value - Breakpoint value (standard grid breakpoint or custom number in pixels)
   * @returns Observable that emits `true` when media query matches, `false` otherwise
   *
   * @example
   *
   * ```typescript
   * // Standard breakpoint usage
   * this.mediaQueryService
   *   .isMediaBreakpointMatches('max-width', 'md')
   *   .pipe(takeUntil(this.destroy$))
   *   .subscribe((matches) => {
   *     // Handle responsive behavior
   *   });
   * ```
   */
  isMediaBreakpointMatches(type: 'max-width' | 'min-width', value: MediaBreakpoints | number): Observable<boolean> {
    const isCustomBreakpoint = typeof value === 'number';

    if (!isCustomBreakpoint && !this.cssVariablesService) {
      throw new Error('In order to use media breakpoints, please add provider for CssVariablesService.');
    }

    const mediaBreakpointValue$ = isCustomBreakpoint
      ? of(`${value}px`)
      : from(this.cssVariablesService.getCssVariable(`--breakpoint-${value}`)).pipe(
          map((value) => {
            if (!value) {
              return value;
            }

            let breakpoint = parseFloat(value);

            if (type === 'max-width' && breakpoint % 1 === 0) {
              breakpoint -= 0.02;
            }

            return `${breakpoint}px`;
          }),
        );

    return mediaBreakpointValue$.pipe(
      switchMap((mediaWidth) => {
        const mediaQueryList = (this.document.defaultView as Window).matchMedia(`(${type}: ${mediaWidth})`);

        return this.getMediaBreakpoints(mediaQueryList);
      }),
    );
  }

  private getMediaBreakpoints(mediaQueryList: MediaQueryList): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      /**
       *   MediaQueryList inherited form EventTarget in some browsers.
       *   Therefore methods like `addEventListener` or `removeEventListener` doesn't work in Safari and IE.
       *   For more details see:
       *   https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList#Browser_compatibility
       */
      const isFunctionAvailable = Boolean(mediaQueryList.addEventListener);
      const mediaListener = ({ matches }: MediaQueryListEvent) => {
        observer.next(matches);

        /**
         * Safari and IE does not inherit Event interface.
         * It means that on media match, event will not be dispatched and Angular will not update UI.
         * Simulating async event to cover this gap.
         */
        if (this.ngZone.isStable) {
          this.ngZone.run(() => Promise.resolve());
        }
      };

      // emit initial match
      observer.next(mediaQueryList.matches);

      if (isFunctionAvailable) {
        mediaQueryList.addEventListener('change', mediaListener);
      } else {
        mediaQueryList.addListener(mediaListener);
      }

      return () => {
        if (isFunctionAvailable) {
          mediaQueryList.removeEventListener('change', mediaListener);
        } else {
          mediaQueryList.removeListener(mediaListener);
        }
      };
    });
  }
}
